import { useCallback, useEffect, useRef, useState } from 'react';
import { useLatest } from '../../hooks/useLatest';
import type { IDataTableFacetValue } from '../DataTable.types';
import { EMPTY_FACET_LABEL } from '../dataTableFilters';
import type { IDataTableFacetPage } from '../dataTableServerSource';

/** Что спрашивает воронка у порционного источника: порция с `skip`, поиск по значениям, отбор по ключам. */
export interface IHeaderFilterPageQuery {
    skip: number;
    take: number;
    search?: string;
    keys?: readonly (string | null)[];
}

export type HeaderFilterPageLoader = (
    columnId: string,
    query: IHeaderFilterPageQuery,
    signal: AbortSignal,
) => Promise<IDataTableFacetPage>;

/** Значение в списке воронки; у отмеченного, которого нет в ответе, счётчик неизвестен. */
export type HeaderFilterValue = Omit<IDataTableFacetValue, 'count'> & { count?: number };

/** Размер порции: несколько экранов попапа, а не весь справочник. */
const HEADER_FILTER_PAGE = 100;
/** Сколько отмеченных значений спрашивать одним запросом (условие «или» по каждому). */
const PINNED_LIMIT = 200;

/**
 * Список воронки у порционного источника. Отмеченные на момент открытия значения идут первыми со своими счётчиками
 * (иначе снять отметку с сотой страницы было бы нельзя) и в порциях не повторяются; отмеченные после открытия
 * остаются на своих местах. Новый поиск, выборка (`scope`) или «Повторить» (`attempt`) начинают список заново.
 */
export function useHeaderFilterPages({
    enabled,
    columnId,
    loadPage,
    search,
    scope,
    attempt,
    selected,
}: {
    enabled: boolean;
    columnId: string;
    loadPage: HeaderFilterPageLoader | undefined;
    /** Поиск, который уходит на сервер (пусто — без поиска). */
    search: string;
    scope?: string;
    attempt: number;
    selected?: readonly (string | null)[];
}) {
    const active = enabled && !!loadPage;
    /** Отмеченные на момент открытия со счётчиками: отметка в открытой воронке не переставляет значения. */
    const [pinned, setPinned] = useState<HeaderFilterValue[] | null>(null);
    const [values, setValues] = useState<IDataTableFacetValue[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [failure, setFailure] = useState<{ error: unknown } | null>(null);
    const selectedRef = useLatest(selected);
    const controllerRef = useRef<AbortController | null>(null);

    const fetchPage = useCallback(
        (skip: number) => {
            if (!loadPage) return;
            controllerRef.current?.abort();
            const controller = new AbortController();
            controllerRef.current = controller;
            setLoading(true);
            setFailure(null);
            loadPage(columnId, { skip, take: HEADER_FILTER_PAGE, search: search || undefined }, controller.signal)
                .then((page) => {
                    if (controller.signal.aborted) return;
                    setValues((prev) => (skip ? [...prev, ...page.values] : page.values));
                    setHasMore(page.hasMore);
                    setLoading(false);
                })
                .catch((error: unknown) => {
                    if (controller.signal.aborted) return;
                    setFailure({ error });
                    setLoading(false);
                });
        },
        [loadPage, columnId, search],
    );

    useEffect(() => {
        setValues([]);
        setHasMore(false);
        setFailure(null);
        setLoading(false);
        if (!active) return;
        fetchPage(0);
        return () => controllerRef.current?.abort();
    }, [active, fetchPage, scope, attempt]);

    // отмеченные — отдельным запросом со своими счётчиками, один раз на открытие и выборку
    useEffect(() => {
        if (!active || !loadPage) {
            setPinned(null);
            return;
        }
        const keys = selectedRef.current ?? [];
        const fallback = (key: string | null): HeaderFilterValue => ({ key, label: key ?? EMPTY_FACET_LABEL });
        if (!keys.length) {
            setPinned([]);
            return;
        }
        setPinned(null);
        const controller = new AbortController();
        const asked = keys.slice(0, PINNED_LIMIT);
        loadPage(columnId, { skip: 0, take: asked.length, keys: asked }, controller.signal)
            .then((page) => {
                if (controller.signal.aborted) return;
                const found = new Map(page.values.map((v) => [v.key, v]));
                // отмеченное, которого нет в выборке (отрезали другие колонки), — со счётчиком 0
                setPinned(
                    keys.map(
                        (key, i) =>
                            found.get(key) ?? (i < PINNED_LIMIT ? { ...fallback(key), count: 0 } : fallback(key)),
                    ),
                );
            })
            .catch(() => {
                if (!controller.signal.aborted) setPinned(keys.map(fallback));
            });
        return () => controller.abort();
    }, [active, loadPage, columnId, scope, attempt, selectedRef]);

    // `skip` следующей порции — сколько уже пришло с сервера (отмеченные лежат отдельно)
    const loadMore = useCallback(() => {
        if (!active || loading || failure || !hasMore) return;
        fetchPage(values.length);
    }, [active, loading, failure, hasMore, fetchPage, values.length]);

    /** «Повторить» после отказа порции: первая — с начала, следующие — та же порция. */
    const retry = useCallback(() => fetchPage(values.length), [fetchPage, values.length]);

    return {
        pinned,
        values,
        hasMore,
        /** Первая порция ещё не пришла (или отмеченные). */
        initialLoading: active && ((loading && !values.length) || pinned == null) && !failure,
        loadingMore: loading && values.length > 0,
        failure,
        loadMore,
        retry,
    };
}
